/**
 * Packed-token (overflow) claim helpers.
 *
 * When a user's org/div access maps exceed the Firebase custom-claims byte budget, the
 * backend drops them from the token and sets `pk: 1` (see services UserClaimsBuilder).
 * Security rules resolve access server-side from users_access, but that document is not
 * client-readable, so the PWA falls back to data it already loads for the signed-in user:
 * - org-level role: `organizations/{orgId}/users/{uid}` (redux `user.orgUser.data.access`)
 * - org membership list: the profile's `organizations` map (`users/{uid}`)
 */

export interface PackableClaims {
  pk?: number;
  org?: Record<string, string | undefined>;
}

export const isPackedClaims = (claims: PackableClaims | null | undefined): boolean =>
  claims?.pk === 1;

/**
 * Org-level access for `orgId`: the token entry wins when present. Only packed
 * tokens omit access maps by design, so only they may use the org-user fallback.
 */
export const resolveOrgAccess = (
  claims: PackableClaims | null | undefined,
  orgId: string | null | undefined,
  fallbackOrgAccess?: string,
): string | undefined => {
  if (!orgId) {
    return undefined;
  }
  const fromClaims = claims?.org?.[orgId];
  if (fromClaims !== undefined) {
    return fromClaims;
  }
  return isPackedClaims(claims) ? fallbackOrgAccess : undefined;
};

/**
 * Organizations the user can see in the org selector. With a full token the claims map is
 * the filter; with a packed token the profile membership map is the source of truth.
 */
export const filterOrganizationsByAccess = <T extends { id?: string }>(
  claims: PackableClaims | null | undefined,
  organizations: Record<string, T | undefined> | null | undefined,
): T[] => {
  const orgs = Object.values(organizations || {}).filter((org): org is T => Boolean(org));
  if (isPackedClaims(claims)) {
    return orgs;
  }
  return orgs.filter(org => Boolean(org.id && claims?.org?.[org.id]));
};
